import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from 'react-query';
import toast from 'react-hot-toast';

import {
  PaginatedNameFilterOptions,
  PaginatedList,
  LeadPartnerDto,
  UpdateLeadPartnerStatusProps,
} from '@project/types';
import { leadPartnerApiService } from '@project/services';

export const useLeadPartnerData = (
  filterOptions: PaginatedNameFilterOptions,
): UseQueryResult<PaginatedList<LeadPartnerDto>, unknown> =>
  useQuery(['lead-partners', filterOptions], () =>
    leadPartnerApiService.getLeadPartner(filterOptions),
  );

type OptimisticStatusContext = {
  previousLeadPartners?: PaginatedList<LeadPartnerDto>;
};

export const useUpdateLeadPartnerStatus = (
  filterOptions: PaginatedNameFilterOptions,
): UseMutationResult<unknown, unknown, UpdateLeadPartnerStatusProps, OptimisticStatusContext> => {
  const queryClient = useQueryClient();
  const queryKey = ['lead-partners', filterOptions];

  return useMutation(
    ({ leadPartnerId, disabled }: UpdateLeadPartnerStatusProps) =>
      leadPartnerApiService.updateLeadPartnerStatus(leadPartnerId, disabled),
    {
      onMutate: async ({ leadPartnerId, disabled }) => {
        await queryClient.cancelQueries(queryKey);

        const previousLeadPartners =
          queryClient.getQueryData<PaginatedList<LeadPartnerDto>>(queryKey);

        if (previousLeadPartners) {
          queryClient.setQueryData<PaginatedList<LeadPartnerDto>>(queryKey, {
            ...previousLeadPartners,
            items: previousLeadPartners.items.map((leadPartner) =>
              leadPartner.id === leadPartnerId ? { ...leadPartner, disabled } : leadPartner,
            ),
          });
        }

        return { previousLeadPartners };
      },
      onError: (_error, _variables, context) => {
        if (context?.previousLeadPartners) {
          queryClient.setQueryData(queryKey, context.previousLeadPartners);
        }
        toast.error('Error updating lead partner status');
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['lead-partners']);
      },
    },
  );
};
